/**
 * SubcontractorModal — row modal for Subcontractor Insurance, mirrors
 * InsuranceModal at a lighter footprint. Provides:
 *   • KPI header cards (Vendor · Type · Expiry Date)
 *   • editable fields (all table columns)
 *   • four attachment sections (versioned)
 *   • change log + notes side panel (via <ChangeLogNotes>)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Paperclip, Eye, Download, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import ChangeLogNotes from "@/components/shared/ChangeLogNotes";

const SECTIONS = [
  { id: "general_liability_workers_comp", label: "General Liability / Workers Comp" },
  { id: "indemnity_agreement",            label: "Indemnity Agreement" },
  { id: "contract",                       label: "Contract (If Applicable)" },
  { id: "additional_documents",           label: "Additional Documents" },
];

function _fmtDateMDY(s) {
  if (!s) return "—";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function SubcontractorModal({ vendor, customCols = [], onClose, onChange }) {
  const [form, setForm] = useState(null);
  const [attachments, setAttachments] = useState({});
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRefs = useRef({});

  useEffect(() => { setForm(vendor ? { ...vendor } : null); setDirty(false); }, [vendor]);

  const loadAtt = useCallback(async (vid) => {
    try {
      const { data } = await api.get(`/subcontractors/${vid}/attachments`);
      setAttachments(data || {});
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (vendor?.id) loadAtt(vendor.id); else setAttachments({}); }, [vendor?.id, loadAtt]);

  if (!vendor || !form) return null;
  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["id","vendor_id","created_at","updated_at","created_by_id","created_by_name","updated_by_id","updated_by_name","has_attachments"].forEach((k) => delete payload[k]);
      const { data } = await api.put(`/subcontractors/${vendor.id}`, payload);
      onChange && onChange(data);
      setDirty(false);
      toast.success("Vendor saved");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const upload = async (section, file) => {
    if (!file) return;
    setUploading(section);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/subcontractors/${vendor.id}/attachments?section=${section}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded to ${section.replace(/_/g, " ")}`);
      await loadAtt(vendor.id);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setUploading(""); }
  };

  const remove = async (fid) => {
    try { await api.delete(`/subcontractors/${vendor.id}/attachments/${fid}`); await loadAtt(vendor.id); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const del = async () => {
    if (!window.confirm(`Delete vendor #${vendor.vendor_id}?`)) return;
    try { await api.delete(`/subcontractors/${vendor.id}`); toast.success("Vendor deleted"); onClose(); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const attachUrl = (fid, dl) => `${backend}/api/subcontractors/${vendor.id}/attachments/${fid}${dl ? "?download=true" : ""}`;

  const kpis = [
    { label: "Vendor",      value: form.vendor || "—" },
    { label: "Type",        value: form.type   || "—" },
    { label: "Expiry Date", value: _fmtDateMDY(form.expiry_date) },
  ];

  return (
    <Dialog open={!!vendor} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl" data-testid="subcontractor-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Vendor #{vendor.vendor_id}{form.vendor ? ` · ${form.vendor}` : ""}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={del} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                <Trash size={12} className="mr-1" /> Delete
              </Button>
              <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="subcontractor-modal-save">
                <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div className="space-y-4">
            {/* KPI cards */}
            <div className="grid grid-cols-3 gap-2" data-testid="subcontractor-modal-kpis">
              {kpis.map((k) => (
                <div key={k.label} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{k.label}</div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5 truncate" title={String(k.value)}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* Fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <F label="Vendor" value={form.vendor} onChange={(v) => setField("vendor", v)} />
              <F label="Type" value={form.type} onChange={(v) => setField("type", v)} />
              <F label="Expiry Date" value={form.expiry_date} onChange={(v) => setField("expiry_date", v)} type="date" />
              <F label="Start Date" value={form.start_date} onChange={(v) => setField("start_date", v)} type="date" />
              <F label="End Date" value={form.end_date} onChange={(v) => setField("end_date", v)} type="date" />
              <F label="Contact" value={form.contact} onChange={(v) => setField("contact", v)} />
              <F label="Phone" value={form.phone} onChange={(v) => setField("phone", v)} type="tel" />
              <F label="Email Address" value={form.email_address} onChange={(v) => setField("email_address", v)} type="email" />
              <F label="Certificate Holder" value={form.certificate_holder} onChange={(v) => setField("certificate_holder", v)} />
              <F label="Indemnity Entity" value={form.indemnity_entity} onChange={(v) => setField("indemnity_entity", v)} />
              <SelectF label="Indemnity Signed" value={form.indemnity_signed} onChange={(v) => setField("indemnity_signed", v)} options={["", "Yes", "No"]} />
              <F label="Insurance Contact Name" value={form.insurance_contact_name} onChange={(v) => setField("insurance_contact_name", v)} />
              <F label="Insurance Contact Phone" value={form.insurance_contact_phone} onChange={(v) => setField("insurance_contact_phone", v)} type="tel" />
              <F label="Insurance Contact Email" value={form.insurance_contact_email} onChange={(v) => setField("insurance_contact_email", v)} type="email" />
            </div>

            {/* Custom fields */}
            {customCols.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Custom fields</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {customCols.map((c) => (
                    <F key={c.id} label={c.label}
                      value={(form.custom_fields || {})[c.key] ?? ""}
                      onChange={(v) => setField("custom_fields", { ...(form.custom_fields || {}), [c.key]: (c.type === "text" || c.type === "date") ? (v || "") : (v === "" || v == null ? null : Number(v)) })}
                      type={c.type === "date" ? "date" : (c.type === "number" || c.type === "money" || c.type === "percent") ? "number" : "text"} />
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-2 mb-2">Documents</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SECTIONS.map((s) => {
                  const versions = attachments[s.id] || [];
                  const current = versions[0];
                  const previous = versions.slice(1);
                  const flat = s.id === "additional_documents";
                  return (
                    <div key={s.id} className="rounded-md border border-slate-200 bg-slate-50/50 p-2" data-testid={`subcontractor-attach-${s.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[11px] font-bold text-slate-700">{s.label}</div>
                        <button type="button" onClick={() => fileRefs.current[s.id]?.click()} disabled={uploading === s.id}
                          className="text-[10px] font-semibold text-[#1f64c2] hover:text-[#327de3] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-white hover:border-[#327de3]">
                          <Paperclip size={10} /> {uploading === s.id ? "Uploading…" : "Add file"}
                        </button>
                        <input ref={(el) => { fileRefs.current[s.id] = el; }} type="file" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(s.id, f); e.target.value = ""; }} />
                      </div>
                      {flat ? (
                        versions.length === 0
                          ? <div className="text-[10px] text-slate-400 italic">No files uploaded.</div>
                          : <div className="space-y-0.5">{versions.map((v) => <Row key={v.id} v={v} attachUrl={attachUrl} onDelete={() => remove(v.id)} hideVersion />)}</div>
                      ) : !current ? (
                        <div className="text-[10px] text-slate-400 italic">No files uploaded.</div>
                      ) : (
                        <>
                          <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-700 mb-0.5">Current version</div>
                          <Row v={current} attachUrl={attachUrl} onDelete={() => remove(current.id)} />
                          {previous.length > 0 && (
                            <details className="mt-1">
                              <summary className="text-[9px] uppercase tracking-widest font-semibold text-slate-500 cursor-pointer">Previous versions ({previous.length})</summary>
                              <div className="mt-1 space-y-0.5 pl-3 border-l border-slate-200">
                                {previous.map((v) => <Row key={v.id} v={v} attachUrl={attachUrl} onDelete={() => remove(v.id)} />)}
                              </div>
                            </details>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <ChangeLogNotes basePath={`/subcontractors/${vendor.id}`} idFieldLabel="vendor" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={13} className="mr-1" /> Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type }) {
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <Input type={type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="text-xs h-7 mt-0.5" />
    </label>
  );
}

function SelectF({ label, value, onChange, options }) {
  return (
    <label className="block text-[10px]">
      <span className="uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="text-xs h-7 mt-0.5 w-full border border-slate-300 rounded-md bg-white px-2">
        {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </label>
  );
}

function Row({ v, attachUrl, onDelete, hideVersion }) {
  return (
    <div className="flex items-center gap-1 text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5">
      {!hideVersion && <span className="font-semibold text-slate-500">v{v.version}</span>}
      <span className="truncate flex-1" title={v.filename}>{v.filename}</span>
      <a href={attachUrl(v.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2] hover:text-[#327de3]"><Eye size={10} /></a>
      <a href={attachUrl(v.id, true)} className="text-[#1f64c2] hover:text-[#327de3]"><Download size={10} /></a>
      <button onClick={onDelete} className="text-rose-500 hover:text-rose-700"><Trash size={10} /></button>
    </div>
  );
}
